/**
 * `useCascadeOnce` — a list cascades in the first time it shows, and never
 * again (#2172, the laptop motion pass; first built on the 4 Oct UI-motion
 * branch).
 *
 * Spread the result on the list's container:
 *
 *   const cascade = useCascadeOnce(rows.length > 0);
 *   <ul data-cascade={cascade}>…</ul>
 *
 * While the value is `"on"`, styles/motion-shell.css rises each child in
 * 30 ms after the one above it (capped at the 12th, so a long table never
 * crawls; opacity and `translate` only). The value turns off once the
 * cascade has played, so a refetch, a sort, a filter or a row added later
 * — any child React moves or mounts afterwards — lands without replaying
 * it. A second list that should carry on the same count (the phone Inbox's
 * Updates under its Needs-you cards) sets `--cascade-base` on its container
 * to the number of rows above it.
 *
 * `ready` holds the cascade back until the list has its first real rows (a
 * list that mounts empty and fills from a query cascades when it fills).
 * Returns `undefined` — no cascade at all — under `prefers-reduced-motion`
 * or under test (`jsMotionEnabled`), so the rows render in their end state.
 *
 * #2172 M3 (the phone app's lists) adds two options:
 *   - `session` — the list's name. A phone tab is mounted again every time
 *     the reader comes back to it; with a name, the cascade plays the first
 *     time that list shows in this opening of the app and never on a later
 *     visit (a tab switched to a hundred times a day must not ripple each
 *     time). Without one it plays once per mount, as on the laptop.
 *   - `replayKey` — the page the list shows (Who's on's day). When it
 *     changes the cascade plays again, for the new page's rows: another day
 *     is a new list, as the Calendar's day strip lines arrive one after
 *     another when another day is picked (M2). Key the list's container on
 *     the same value so its rows are new elements and the CSS replays.
 */
import { useEffect, useState } from "react";

import { DUR_PRESS_MS, DUR_SLOW_MS, jsMotionEnabled } from "./motionTokens";

/** The delay between two rows, and the row from which they all start
 *  together. Mirrors motion-shell.css. */
export const CASCADE_STEP_MS = 30;
export const CASCADE_CAP = 12;
/** Long enough for the last capped row to finish. */
export const CASCADE_TOTAL_MS = CASCADE_CAP * CASCADE_STEP_MS + DUR_SLOW_MS + DUR_PRESS_MS;

type Phase = "pending" | "on" | "done";

export interface CascadeOptions {
  /** Play once per opening of the app for the list of this name (M3). */
  session?: string | undefined;
  /** Play again whenever this changes — the list is showing another page. */
  replayKey?: string | undefined;
  /** Overrides the environment check (the hook's own tests). */
  enabled?: boolean | undefined;
}

/** The named lists that have cascaded in this opening of the app. */
const cascadedThisSession = new Set<string>();

/** Tests only: forget which lists have cascaded. */
export function resetCascadeSessionForTests(): void {
  cascadedThisSession.clear();
}

/**
 * `options` may still be the plain `enabledOverride` boolean the laptop's
 * tests pass.
 */
export function useCascadeOnce(
  ready = true,
  options: boolean | CascadeOptions = {},
): "on" | undefined {
  const opts: CascadeOptions = typeof options === "boolean" ? { enabled: options } : options;
  const { session, replayKey } = opts;
  const [enabled] = useState(() => opts.enabled ?? jsMotionEnabled());
  const [phase, setPhase] = useState<Phase>(() =>
    session && cascadedThisSession.has(session) ? "done" : ready ? "on" : "pending",
  );
  const [shownKey, setShownKey] = useState(replayKey);
  // Another page: its rows cascade in (or wait for them, if it is still empty).
  if (replayKey !== shownKey) {
    setShownKey(replayKey);
    setPhase(ready ? "on" : "pending");
  }
  // The first render with rows turns it on before that render commits, so
  // the rows' first paint is already the cascade's first frame.
  else if (ready && phase === "pending") setPhase("on");

  // biome-ignore lint/correctness/useExhaustiveDependencies: `shownKey` restarts the clock for a new page's rows.
  useEffect(() => {
    if (!enabled || phase !== "on") return;
    if (session) cascadedThisSession.add(session);
    const timer = window.setTimeout(() => setPhase("done"), CASCADE_TOTAL_MS);
    return () => window.clearTimeout(timer);
  }, [phase, enabled, session, shownKey]);

  return enabled && phase === "on" ? "on" : undefined;
}
